import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  ErrorBoundary,
  SportradarFactory,
  MEDIA_BREAKPOINTS,
  MEDIUM_SCREEN_SIZE,
  SMALL_SCREEN_SIZE,
  LARGE_SCREEN_SIZE,
} from 'mm-ui-components';
import { Store } from 'redux';
import { StyleSheet, css } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SLOT_NAMES } from '../templates.const';
import { Page } from '../Page';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
} from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
  getMetadataDescription,
  getMetadataImage,
  getMetadataTitle,
  getSportradarProps,
} from '../../store/template/widgetPage/widgetPageDublin/widgetPageDublin.selectors';
import { createSportradarProps, createWidgetName, createStatsList } from './dublinTemplate.utils';

interface WidgetPageDublinProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const WidgetPageDublinComponent: React.FunctionComponent<
  WidgetPageDublinProps
> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <WidgetPageDublinContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
    widgetWrapper: {
      margin: '20px 0',
      boxShadow: 'none',
    },
    sectionWrapper: {
      maxWidth: `${LARGE_SCREEN_SIZE}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        maxWidth: `${MEDIUM_SCREEN_SIZE}px`,
        padding: '0 20px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        maxWidth: `${SMALL_SCREEN_SIZE}px`,
        padding: '0',
      },
      display: 'flex',
      flexDirection: 'column',
      justifyItems: 'left',
      margin: '0 auto 10px',
    },
    noStyles: {
      boxShadow: 'none',
      margin: '0',
      padding: '0',
    },
    padding: {
      padding: '20px 0',
    },
    header: {
      flex: '1 1 auto',
      [MEDIA_BREAKPOINTS.medium]: {
        padding: '20px 30px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: '20px',
      },
    },
    navContainer: {
      margin: '0 0 20px',
      backgroundColor: 'white',
      boxShadow: '0 4px 4px -2px #ddd',
    },
    navSubContainer: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      maxWidth: LARGE_SCREEN_SIZE,
      margin: '0 auto',
      padding: '0',
      justifyContent: 'flex-end',
    },
  });

export const WidgetPageDublinContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const metadataDescription = useSelector(getMetadataDescription);
  const metadataTitle = useSelector(getMetadataTitle);
  const metadataImage = useSelector(getMetadataImage);

  const canonical = useSelector(getCanonicalUrl);
  const sportradarProps = useSelector(getSportradarProps);
  const props = createSportradarProps(canonical, sportradarProps);
  const { league, widget } = props || {};

  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags
        title={metadataTitle}
        description={metadataDescription}
        pageImage={metadataImage}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout
            mainWrapperConfigThemeKey="mainContent"
            isFullBleed
            noMarginTop
          >
            {props?.league && (
              <div className={css(style.navContainer)}>
                <div className={css(style.navSubContainer)}>
                  <SportradarFactory
                    sportradarProps={props}
                    name={createWidgetName('_TEAM_HEADER', league)}
                    wrapperStyle={[style.noStyles, style.padding, style.header]}
                    widgetStyle={style.noStyles}
                  />
                </div>
              </div>
            )}
            <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
            {props?.league && (
            <div className={css(style.sectionWrapper)}>
              {widget === 'STATS' ? (
                <SportradarFactory
                  sportradarList={createStatsList(league, props)}
                  wrapperStyle={style.widgetWrapper}
                />
              ) : (
                <SportradarFactory
                  name={createWidgetName(`_${widget}`, league)}
                  sportradarProps={props}
                  wrapperStyle={style.widgetWrapper}
                />
              )}
            </div>
            )}

          </NavigationLayout>
          <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
